import Vue from 'vue';

/**
 * Build a contextual user profile string from the Vue component instance.
 * Pass `this` from any card component that has the relevant injects.
 *
 * @param {object} vm - Vue component instance
 * @param {object} extra - Additional context: { kpis, habits, growthAreas }
 * @returns {string}
 */
export function buildUserContext(vm, extra = {}) {
  const store = vm.$store;
  const user = store?.state?.auth?.user || {};
  const stats = store?.state?.auth?.userStats || {};

  const roleName = (user.role_id === 1 || user.role_id === 11) ? 'Admin'
    : user.role_id === 2 ? 'Manager'
    : 'Employee';

  const today = new Date().toISOString().slice(0, 10);
  const todayLabel = new Date().toLocaleDateString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });

  // Priority name map
  const priName = { 1: 'Urgent', 2: 'High', 3: 'Medium', 4: 'Low' };
  const daysBetween = (iso) => {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    return Math.round((d.getTime() - Date.now()) / (1000 * 3600 * 24));
  };

  const lines = [
    `## CONTEXT\n- Today: ${todayLabel}`,
    `\n## USER PROFILE\n- Name: ${user.name || 'Unknown'}\n- Role: ${roleName}`,
    user.position ? `- Position: ${user.position}` : null,
    user.department ? `- Department: ${user.department}` : null,
  ];

  // Task workload summary
  if (Object.keys(stats).length) {
    lines.push(
      `\n## TASK WORKLOAD\n- Todo: ${stats.todo_tasks || 0}\n- In Progress: ${stats.in_progress_tasks || 0}\n- Overdue: ${stats.overdue_tasks || 0}\n- Completed (all time): ${stats.completed_tasks || 0}`
    );
  }

  // Live tasks from inject
  if (typeof vm.personalTasks === 'function' || typeof vm.assignedTasks === 'function') {
    const all = [
      ...(typeof vm.personalTasks === 'function' ? vm.personalTasks() : []),
      ...(typeof vm.assignedTasks === 'function' ? vm.assignedTasks() : []),
    ].filter(t => !t.deleted_at);

    // Overdue tasks — send all so AI count matches the prompt
    const overdue = all
      .filter(t => !t.completed_at && t.due_date && String(t.due_date).slice(0, 10) < today)
      .sort((a, b) => String(a.due_date).localeCompare(String(b.due_date)))
      .map(t => {
        const daysLate = Math.abs(daysBetween(t.due_date) || 0);
        return `- "${t.name}" [${priName[t.priority_id] || 'Normal'}] — ${daysLate}d overdue (due ${String(t.due_date).slice(0, 10)})`;
      });
    if (overdue.length) lines.push(`\n## OVERDUE TASKS (${overdue.length} shown)\n${overdue.join('\n')}`);

    // Due today
    const dueToday = all
      .filter(t => !t.completed_at && t.due_date && String(t.due_date).slice(0, 10) === today)
      .slice(0, 10)
      .map(t => `- "${t.name}" [${priName[t.priority_id] || 'Normal'}] (${t.status?.name || 'To Do'})`);
    if (dueToday.length) lines.push(`\n## DUE TODAY\n${dueToday.join('\n')}`);

    // Upcoming (next 7 days, excluding today)
    const upcoming = all
      .filter(t => !t.completed_at && t.due_date && String(t.due_date).slice(0, 10) > today)
      .filter(t => (daysBetween(t.due_date) ?? 999) <= 7)
      .sort((a, b) => String(a.due_date).localeCompare(String(b.due_date)))
      .slice(0, 8)
      .map(t => {
        const d = daysBetween(t.due_date);
        return `- "${t.name}" [${priName[t.priority_id] || 'Normal'}] — in ${d}d (${String(t.due_date).slice(0, 10)})`;
      });
    if (upcoming.length) lines.push(`\n## UPCOMING (next 7 days)\n${upcoming.join('\n')}`);

    // In-progress right now (no due filter)
    const inProgress = all
      .filter(t => !t.completed_at && (t.status?.name || '').toLowerCase().includes('progress'))
      .slice(0, 6)
      .map(t => `- "${t.name}" [${priName[t.priority_id] || 'Normal'}]`);
    if (inProgress.length) lines.push(`\n## IN PROGRESS RIGHT NOW\n${inProgress.join('\n')}`);

    // Completed in last 7 days (recent wins)
    const weekAgo = new Date(Date.now() - 7 * 24 * 3600 * 1000).toISOString().slice(0, 10);
    const recentWins = all
      .filter(t => t.completed_at && String(t.completed_at).slice(0, 10) >= weekAgo)
      .slice(0, 6)
      .map(t => `- "${t.name}" [${priName[t.priority_id] || 'Normal'}]`);
    if (recentWins.length) lines.push(`\n## RECENT WINS (last 7 days)\n${recentWins.join('\n')}`);

    // Completed today (for momentum)
    const completedToday = all.filter(t => t.completed_at && String(t.completed_at).slice(0, 10) === today).length;
    lines.push(`\n## TODAY'S MOMENTUM\n- Tasks completed today: ${completedToday}\n- Tasks due today remaining: ${dueToday.length}\n- Overdue count: ${overdue.length}`);
  }

  // KPIs
  if (extra.kpis?.length) {
    const done  = extra.kpis.filter(k => k.is_done).length;
    const total = extra.kpis.length;
    const list  = extra.kpis.map(k => `- [${k.is_done ? 'x' : ' '}] ${k.text}`).join('\n');
    lines.push(`\n## THIS WEEK'S KPIS (${done}/${total} done)\n${list}`);
  } else {
    lines.push(`\n## THIS WEEK'S KPIS\n- None set yet`);
  }

  // Habits
  if (extra.habits?.length) {
    const list = extra.habits
      .map(h => `- ${h.text} — ${h.streak || 0}d streak${h.completed_today ? ' ✓ done today' : ''}`)
      .join('\n');
    lines.push(`\n## DAILY HABITS\n${list}`);
  } else {
    lines.push(`\n## DAILY HABITS\n- None tracked yet`);
  }

  // Growth areas
  if (extra.growthAreas?.length) {
    const list = extra.growthAreas
      .map(g => `- [${g.is_done ? 'x' : ' '}] ${g.text}`)
      .join('\n');
    lines.push(`\n## GROWTH AREAS\n${list}`);
  }

  const context = lines.filter(Boolean).join('\n');
  console.log('[growthAiService] buildUserContext:', context);
  return context;
}

/**
 * Call OpenAI via the Laravel backend proxy (avoids browser CORS).
 * Laravel forwards the request server-to-server to the Cloud Function.
 * @param {string} userMessage
 * @param {string} systemPrompt
 * @param {object} [opts]
 * @param {boolean} [opts.logSession=false] When true, also POSTs an AI session
 *   to `api/employee-growth/ai-sessions` so the Growth Score engine can award
 *   AI-pillar points per the Growth Score spec (1 use = 2 pts normal, 5 pts
 *   high-impact; backend enforces the 20 pts/day cap).
 * @param {boolean} [opts.isHighImpact=false] Flags the session as high-impact.
 *   Use for goal-directed coaching (KPI suggest, Habit suggest, Growth-area
 *   suggest, AI Coach prompts) — not for idle/exploratory queries.
 * @returns {Promise<string>} AI text response
 */
export async function askAI(userMessage, systemPrompt = '', opts = {}) {
  const input = [];
  if (systemPrompt) input.push({ role: 'system', content: systemPrompt });
  input.push({ role: 'user', content: userMessage });

  const payload = { model: 'gpt-5.4-mini', input };
  console.log('[growthAiService] askAI request:', payload);

  const res = await Vue.axios.post('api/ai-proxy', payload);

  console.log('[growthAiService] askAI response:', res.data);

  // OpenAI Responses API: output[0].content[0].text
  const text = res.data?.output?.[0]?.content?.[0]?.text;
  if (!text) throw new Error('No response from AI');

  // Fire-and-forget session log so the Growth Score AI pillar accrues points.
  // Errors are swallowed — scoring must never block the user's AI reply.
  if (opts.logSession) {
    const sessionPayload = {
      prompt: userMessage,
      response: text,
      is_high_impact: !!opts.isHighImpact,
    };
    Vue.axios
      .post('api/employee-growth/ai-sessions', sessionPayload)
      .catch((err) => {
        console.warn('[growthAiService] ai-session log failed:', err?.message || err);
      });
  }

  return text;
}

/**
 * Parse a JSON block from AI response (handles markdown code fences).
 */
export function parseAIJson(text) {
  const clean = text.replace(/```json|```/g, '').trim();
  return JSON.parse(clean);
}
